import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ProtectedShell } from "@/features/auth/ProtectedShell";
import { Panel } from "@/components/ui/Panel";
import { Metric } from "@/components/ui/Metric";
import { DataTable } from "@/components/ui/DataTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Sparkline } from "@/components/ui/Sparkline";
import { Delta } from "@/components/ui/Delta";
import { useToast } from "@/features/toast/ToastProvider";
import { useConfirm } from "@/features/confirm/ConfirmProvider";
import { api, ApiError } from "@/lib/api";
import { formatDate, formatMoney } from "@bookboost/shared/format";
import type { SparkPoint } from "@bookboost/shared/chart";

interface DealRow {
  id: string;
  deal_name: string | null;
  amount: number | null;
  currency: string | null;
  stage: string | null;
  stage_changed_at: string | null;
  closed_won_at: string | null;
  onboarding_waived: boolean;
  invoices: Array<{ id: string; status: string; paid_at: string | null; has_onboarding: boolean }>;
}

interface DashboardResponse {
  user: { id: string; email: string; full_name: string | null; role: string };
  year: number;
  structure: {
    structure_type: "flat" | "split_equity";
    base_commission_rate: number;
    equity_split_rate: number | null;
    equity_annual_cap: number | null;
    cap_currency: string | null;
    effective_from: string | null;
  } | null;
  // Headline summary converted into the rep's salary (payout) currency.
  salaryCurrency: string;
  salaryTotals: { earned: number; cash: number; equity: number; outstanding: number };
  // Currencies that couldn't be FX-converted (no rate) and are excluded from the summary.
  fxMissing: string[];
  // Raw per-currency totals, shown in the breakdown drop-down.
  totals: Record<string, { earned: number; cash: number; equity: number; outstanding: number }>;
  commissions: Array<{
    id: string;
    deal_id: string | null;
    deal_amount: number | null;
    commission_total: number;
    cash_portion: number;
    equity_portion: number;
    currency: string;
    status: "pending" | "earned" | "approved" | "paid";
    earned_at: string | null;
    paid_at: string | null;
    notes: string | null;
    deals: { deal_name: string | null; hubspot_deal_id: string } | null;
  }>;
  trailing: SparkPoint[];
  yoyDelta: { pct: number; direction: "up" | "down" | "flat"; label: string } | null;
  deals: DealRow[];
}

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  return (
    <ProtectedShell>
      <DashboardContent />
    </ProtectedShell>
  );
}

function DashboardContent() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();

  const { data, isLoading, error } = useQuery({
    queryKey: ["dashboard", "me"],
    queryFn: () => api.get<DashboardResponse>("/api/dashboard/me"),
  });

  const markOnboardingFree = useMutation({
    mutationFn: (dealId: string) => api.post("/api/dashboard/onboarding-free", { deal_id: dealId }),
    onSuccess: () => {
      toast.ok("Marked as free onboarding.");
      qc.invalidateQueries({ queryKey: ["dashboard", "me"] });
    },
    onError: (err) => toast.err(err instanceof ApiError ? err.message : "Couldn't mark."),
  });

  if (isLoading) return <p className="lede">Loading your commissions…</p>;
  if (error || !data) {
    return (
      <Panel variant="err">
        <h2>Couldn&apos;t load your dashboard</h2>
        <p>{error instanceof Error ? error.message : "Please try again."}</p>
      </Panel>
    );
  }

  const totalsEntries = Object.entries(data.totals);
  // Offer the per-currency breakdown only when it adds something: more than one
  // currency, or a single currency that differs from the salary currency.
  const showBreakdown =
    totalsEntries.length > 1 ||
    (totalsEntries.length === 1 && totalsEntries[0]![0].toUpperCase() !== data.salaryCurrency);
  const trailingTotal = data.trailing.reduce((acc, p) => acc + p.value, 0);
  const peakLabel = data.trailing.reduce((acc, p) => (p.value > acc.value ? p : acc), data.trailing[0] ?? { label: "", value: 0 });

  return (
    <>
      <h1>
        My commissions · <span style={{ color: "var(--muted)", fontWeight: 500 }}>{data.year}</span>
      </h1>

      {totalsEntries.length === 0 ? (
        <Panel>
          <EmptyState
            icon="commissions"
            title="No commissions yet this year"
            body="As your closed-won deals get paid, commission rows will appear here."
          />
        </Panel>
      ) : (
        <>
          <p className="muted" style={{ marginTop: -4, marginBottom: 14, fontSize: 13 }}>
            Totalled in <strong>{data.salaryCurrency}</strong> — your payout currency.
          </p>
          <div className="grid cols-4">
            <Metric
              label="Earned"
              value={formatMoney(data.salaryTotals.earned, data.salaryCurrency)}
              hint="Total commissions earned this calendar year, converted to your salary currency."
            />
            <Metric
              label="Cash portion"
              value={formatMoney(data.salaryTotals.cash, data.salaryCurrency)}
              hint="Cash share of earned commissions, before payout."
            />
            <Metric
              label="Equity portion"
              value={formatMoney(data.salaryTotals.equity, data.salaryCurrency)}
              hint="Equity-equivalent portion. Reporting only; payout still happens in cash."
            />
            <Metric
              label="Outstanding"
              value={formatMoney(data.salaryTotals.outstanding, data.salaryCurrency)}
              tone="accent"
              hint="Earned + approved commissions not yet marked paid by finance."
            />
          </div>

          {data.fxMissing.length > 0 && (
            <p className="muted" style={{ marginTop: 8, fontSize: 12.5 }}>
              Excludes {data.fxMissing.join(", ")} — no FX rate yet, so not in the total above. Still shown in the breakdown.
            </p>
          )}

          {showBreakdown && (
            <details style={{ marginTop: 14 }}>
              <summary
                className="muted"
                style={{ cursor: "pointer", fontSize: 13, userSelect: "none", marginBottom: 4 }}
              >
                Show currency breakdown
              </summary>
              <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
                {totalsEntries.map(([currency, t]) => (
                  <div className="grid cols-4" key={currency}>
                    <Metric
                      label={`Earned · ${currency}`}
                      value={formatMoney(t.earned, currency)}
                      hint="Total commissions earned this calendar year on this currency."
                    />
                    <Metric
                      label="Cash portion"
                      value={formatMoney(t.cash, currency)}
                      hint="Cash share of earned commissions, before payout."
                    />
                    <Metric
                      label="Equity portion"
                      value={formatMoney(t.equity, currency)}
                      hint="Equity-equivalent portion. Reporting only; payout still happens in cash."
                    />
                    <Metric
                      label="Outstanding"
                      value={formatMoney(t.outstanding, currency)}
                      tone="accent"
                      hint="Earned + approved commissions not yet marked paid by finance."
                    />
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}

      {data.trailing.length > 0 && trailingTotal > 0 && (
        <Panel title="Trailing 13 months">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Peak: <strong>{formatMoney(peakLabel.value, data.salaryCurrency)}</strong> ({peakLabel.label})
            </div>
            {data.yoyDelta && <Delta delta={data.yoyDelta} suffix="vs last year" />}
          </div>
          <Sparkline points={data.trailing} peak caption={`Sum: ${formatMoney(trailingTotal, data.salaryCurrency)} · monthly buckets`} />
        </Panel>
      )}

      {data.structure && (
        <Panel title="Active commission structure">
          <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "8px 18px", margin: 0 }}>
            <dt className="muted">Type</dt>
            <dd style={{ margin: 0 }}>{data.structure.structure_type}</dd>
            <dt className="muted">Base rate</dt>
            <dd style={{ margin: 0 }}>{(data.structure.base_commission_rate * 100).toFixed(2)}%</dd>
            {data.structure.structure_type === "split_equity" && data.structure.equity_split_rate != null && (
              <>
                <dt className="muted">Equity split</dt>
                <dd style={{ margin: 0 }}>{(data.structure.equity_split_rate * 100).toFixed(0)}%</dd>
              </>
            )}
            {data.structure.equity_annual_cap != null && (
              <>
                <dt className="muted">Annual equity cap</dt>
                <dd style={{ margin: 0 }}>{formatMoney(data.structure.equity_annual_cap, data.structure.cap_currency ?? "EUR")}</dd>
              </>
            )}
            <dt className="muted">Effective from</dt>
            <dd style={{ margin: 0 }}>{formatDate(data.structure.effective_from)}</dd>
          </dl>
        </Panel>
      )}

      <Panel title="Recent commissions" padding="tight">
        <div style={{ display: "flex", justifyContent: "flex-end", padding: "0 0 8px" }}>
          <a href="/api/export/commissions.csv" className="statement-link">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M7 2v7m0 0l-3-3m3 3l3-3M2 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Download my statement (CSV)
          </a>
        </div>
        {data.commissions.length === 0 ? (
          <EmptyState icon="commissions" title="No rows" tone="subtle" />
        ) : (
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Earned</th>
                  <th>Deal</th>
                  <th>Status</th>
                  <th>Cash</th>
                  <th>Equity</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {data.commissions.map((c) => (
                  <tr key={c.id}>
                    <td data-label="Earned" className="muted">{formatDate(c.earned_at)}</td>
                    <td data-label="Deal">
                      <Link to="/dashboard/commissions/$id" params={{ id: c.id }} className="commission-link">
                        {c.deals?.deal_name ?? "View breakdown"}
                      </Link>
                    </td>
                    <td data-label="Status"><StatusBadge status={c.status} /></td>
                    <td data-label="Cash">{formatMoney(c.cash_portion, c.currency)}</td>
                    <td data-label="Equity">{formatMoney(c.equity_portion, c.currency)}</td>
                    <td data-label="Total"><strong>{formatMoney(c.commission_total, c.currency)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTable>
        )}
      </Panel>

      {data.deals.length > 0 && (
        <Panel title="My deals" padding="tight">
          <p className="muted" style={{ fontSize: 12.5, margin: 0, padding: "0 4px 10px", lineHeight: 1.5 }}>
            <strong>Onboarding</strong> tracks each deal's onboarding invoice — <em>not issued</em>, <em>issued</em>,{" "}
            <em>paid</em>, or <em>free</em>. If a deal won't be billed for onboarding, use <strong>Mark onboarding as free</strong>{" "}
            so finance and reporting know no onboarding invoice is coming. Reverting that needs an admin.
          </p>
          <DataTable>
            <table className="table-cards">
              <thead>
                <tr>
                  <th>Deal</th>
                  <th>Stage</th>
                  <th>Amount</th>
                  <th>Onboarding</th>
                  <th>Closed-won</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.deals.map((d) => {
                  const onboardingInvoice = d.invoices.find((i) => i.has_onboarding);
                  const onboardingStatus = d.onboarding_waived
                    ? "free"
                    : onboardingInvoice
                      ? onboardingInvoice.status === "paid"
                        ? "paid"
                        : "issued"
                      : "not issued";
                  return (
                    <tr key={d.id}>
                      <td data-label="Deal"><strong>{d.deal_name ?? "—"}</strong></td>
                      <td data-label="Stage" className="muted">{d.stage ?? "—"}</td>
                      <td data-label="Amount">{formatMoney(d.amount, d.currency)}</td>
                      <td data-label="Onboarding">
                        <span
                          className="badge"
                          style={
                            onboardingStatus === "free"
                              ? { background: "var(--primary50)", color: "var(--accent-deep)", borderColor: "var(--primary200)" }
                              : onboardingStatus === "paid"
                                ? { background: "var(--accent-2-soft)", color: "var(--accent-2)" }
                                : onboardingStatus === "issued"
                                  ? { background: "var(--warn-soft)", color: "var(--warn)" }
                                  : undefined
                          }
                        >
                          {onboardingStatus}
                        </span>
                      </td>
                      <td data-label="Closed-won" className="muted">{formatDate(d.closed_won_at)}</td>
                      <td data-label="Actions" style={{ textAlign: "right" }}>
                        {!d.onboarding_waived && !onboardingInvoice && (
                          <button
                            style={{ padding: "4px 10px", fontSize: 12 }}
                            title="No onboarding invoice expected for this deal — mark its onboarding as free. Reverting needs an admin."
                            disabled={markOnboardingFree.isPending}
                            onClick={async () => {
                              const ok = await confirm({
                                title: "Mark onboarding as free",
                                prompt: `Confirm ${d.deal_name ?? "this deal"} won't be billed for onboarding (no onboarding invoice expected). This is informational — it doesn't change commissions. Reverting needs an admin.`,
                                confirmLabel: "Mark free",
                              });
                              if (ok) markOnboardingFree.mutate(d.id);
                            }}
                          >
                            Mark onboarding as free
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </DataTable>
        </Panel>
      )}
    </>
  );
}
